import {AgentType} from 'graphql-client/__generated/graphqlGlobalTypes';
import type {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

export interface ChatInputTip {
  id: string;
  icon: BenchlingIconName;
  title: string;
  text: string;
  /**
   * Agent types referenced by the tip. The tip will only be shown if ALL listed agent
   * types are enabled for the tenant/user (see `isAgentTypeEnabled`).
   */
  requiredAgentTypes?: ReadonlyArray<AgentType>;
  /**
   * If true, the tip is only shown when image generation in Deep Research is
   * available (gated by `BenchlingConfig.LLM_ENABLE_GOOGLE_CLOUD`).
   */
  requiresImageGeneration?: boolean;
}

const CHAT_INPUT_TIPS: ReadonlyArray<ChatInputTip> = [
  {
    id: '1',
    icon: 'lightbulb',
    title: 'Skills',
    text: 'Level up your agent with Skills \u2014 reusable prompts that shape how agents respond. Click the gear icon in the top right to view or add them.',
  },
  {
    id: '2',
    icon: 'settings',
    title: 'Customize behavior',
    text: "Make Benchling AI work the way you do. Click the gear icon in the top right to customize your agent's tone, persona, and more.",
  },
  {
    id: '3',
    icon: 'line-chart',
    title: 'Dynamic plots',
    text: 'Use Deep Research to turn your data into interactive charts and visualizations.',
    requiredAgentTypes: [AgentType.DEEP_RESEARCH],
  },
  {
    id: '4',
    icon: 'protocol-image',
    title: 'Image generation',
    text: 'Use Deep Research to generate illustrations or figures for your experiments.',
    requiredAgentTypes: [AgentType.DEEP_RESEARCH],
    requiresImageGeneration: true,
  },
  {
    id: '5',
    icon: 'sparkles',
    title: 'Deep Research',
    text: 'Ask complex questions and dig deeper into your data by selecting Deep Research.',
    requiredAgentTypes: [AgentType.DEEP_RESEARCH],
  },
  {
    id: '6',
    icon: 'file-arrow-up',
    title: 'File upload',
    text: 'Drag and drop files directly into the chat. Upload templates, protocols, or files with context to get more relevant answers.',
  },
  {
    id: '7',
    icon: 'warehouse-table',
    title: 'Data capture',
    text: "Use Compose to import data directly into your draft's structured tables.",
    requiredAgentTypes: [AgentType.DATA_CAPTURE],
  },
  {
    id: '8',
    icon: 'lightbulb',
    title: 'Help Center',
    text: 'Use Ask to learn how a feature works on Benchling without leaving your workspace.',
    requiredAgentTypes: [AgentType.ASK],
  },
  {
    id: '9',
    icon: 'save',
    title: 'Saving responses',
    text: 'Click "Save as" in the Deep Research response to add the content to a new entry, new template, new Word document, or an existing entry.',
    requiredAgentTypes: [AgentType.DEEP_RESEARCH],
  },
  {
    id: '10',
    icon: 'edit',
    title: 'Kickstart drafting',
    text: 'Iterate on your work by pasting a link to a previous entry or template in your Compose chat.',
    requiredAgentTypes: [AgentType.DATA_CAPTURE],
  },
];

export default CHAT_INPUT_TIPS;
